import { useState, useEffect, useRef, useCallback } from 'react'
import { Room, nullRoom } from '../Types'
import { apiClient } from '../api/client'
import { initRoomFromData } from '../api/utils'

interface SSEHookOptions {
    gameId: string
    playerId?: string
    onUpdate?: (room: Room) => void
    onError?: (error: Error) => void
    enabled?: boolean
}

interface SSEHookReturn {
    room: Room
    error: Error | null
    isConnected: boolean
    reconnect: () => void
}

/**
 * Custom hook for Server-Sent Events (SSE) real-time updates
 * Replaces Firebase onValue listener
 */
export function useSSE({
    gameId,
    playerId,
    onUpdate,
    onError,
    enabled = true,
}: SSEHookOptions): SSEHookReturn {
    const [room, setRoom] = useState<Room>(nullRoom)
    const [error, setError] = useState<Error | null>(null)
    const [isConnected, setIsConnected] = useState(false)

    const eventSourceRef = useRef<EventSource | null>(null)
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const reconnectAttempts = useRef(0)
    const maxReconnectAttempts = 5

    // Use refs for callbacks to avoid dependency issues
    const onUpdateRef = useRef(onUpdate)
    const onErrorRef = useRef(onError)

    // Update refs when callbacks change
    useEffect(() => {
        onUpdateRef.current = onUpdate
        onErrorRef.current = onError
    }, [onUpdate, onError])

    const cleanup = useCallback(() => {
        if (eventSourceRef.current) {
            console.log(`[SSE] Closing connection to room ${gameId}`)
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        setIsConnected(false)
    }, [gameId])

    const connect = useCallback(() => {
        if (!enabled || !gameId) {
            return
        }

        // Close existing connection
        cleanup()

        try {
            const url = apiClient.getStreamUrl(gameId, playerId)
            console.log(`[SSE] Connecting to ${url}`)

            const eventSource = new EventSource(url)
            eventSourceRef.current = eventSource

            eventSource.onopen = () => {
                console.log(`[SSE] Connected to room ${gameId}`)
                console.log(`[SSE] EventSource readyState: ${eventSource.readyState}`)
                setIsConnected(true)
                setError(null)
                reconnectAttempts.current = 0
            }

            eventSource.onmessage = (event) => {
                

                try {
                    const data = JSON.parse(event.data)
                    console.log('[SSE] Message received:', data)


                    if (data.type === 'room_update') {
                        // Transform the room data (reconstruct Deck instances, etc.)
                        

                        const transformedRoom = initRoomFromData(data.data)
                        

                        setRoom(transformedRoom)
                        onUpdateRef.current?.(transformedRoom)
                    } else if (data.type === 'error') {
                        const err = new Error(data.data?.message || 'SSE error')
                        setError(err)
                        onErrorRef.current?.(err)
                    }
                } catch (err) {
                    console.error('[SSE] Error parsing message:', err)
                    console.error('[SSE] Event data was:', event.data)
                    const parseError = err instanceof Error ? err : new Error('Failed to parse SSE message')
                    setError(parseError)
                    onErrorRef.current?.(parseError)
                }
            }

            eventSource.onerror = (event) => {
                console.error('[SSE] Connection error:', event)
                console.error('[SSE] EventSource readyState:', eventSource.readyState)
                console.error('[SSE] URL was:', url)
                setIsConnected(false)

                const connectionError = new Error('SSE connection lost')
                setError(connectionError)
                onErrorRef.current?.(connectionError)

                // Attempt to reconnect with exponential backoff
                if (reconnectAttempts.current < maxReconnectAttempts) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
                    console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`)

                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttempts.current++
                        connect()
                    }, delay)
                } else {
                    console.error('[SSE] Max reconnect attempts reached')
                    cleanup()
                }
            }
        } catch (err) {
            console.error('[SSE] Failed to create EventSource:', err)
            const createError = err instanceof Error ? err : new Error('Failed to create SSE connection')
            setError(createError)
            onErrorRef.current?.(createError)
        }
    }, [enabled, gameId, playerId, cleanup])

    const reconnect = useCallback(() => {
        

        reconnectAttempts.current = 0
        connect()
    }, [connect])

    // Connect on mount, disconnect on unmount
    useEffect(() => {
        connect()
        return cleanup
    }, [connect, cleanup])

    return {
        room,
        error,
        isConnected,
        reconnect,
    }
}

export default useSSE
